import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Send,
  Calendar,
  BarChart3,
  Layers,
  Link2,
  Sparkles,
  Users,
  Globe,
  ArrowRight,
  Check,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import PlugDLogo from "../common/PlugDLogo";
import MarketingHeader from "../marketing/layout/MarketingHeader";

export default function LandingPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const PLANS = [
    {
      name: t("land_plan_free_name", "Free"),
      price: "$0",
      period: t("land_plan_free_period", "/forever"),
      description: t("land_plan_free_desc", "Perfect for getting started"),
      features: [
        t("land_plan_free_f1", "2 social accounts"),
        t("land_plan_free_f2", "10 scheduled posts/month"),
        t("land_plan_free_f3", "1 link-in-bio page"),
        t("land_plan_free_f4", "5 short links"),
        t("land_plan_free_f5", "10 AI generations"),
        t("land_plan_free_f6", "100 MB storage"),
      ],
      cta: t("land_plan_free_cta", "Get Started Free"),
      highlighted: false,
    },
    {
      name: t("land_plan_start_name", "Start"),
      price: "$15",
      period: t("land_plan_start_period", "/month"),
      description: t("land_plan_start_desc", "For creators growing their audience"),
      features: [
        t("land_plan_start_f1", "5 social accounts"),
        t("land_plan_start_f2", "50 scheduled posts/month"),
        t("land_plan_start_f3", "3 link-in-bio pages"),
        t("land_plan_start_f4", "50 short links"),
        t("land_plan_start_f5", "100 AI generations"),
        t("land_plan_start_f6", "1 GB storage"),
        t("land_plan_start_f7", "2 team members"),
      ],
      cta: t("land_plan_start_cta", "Start Free Trial"),
      highlighted: false,
    },
    {
      name: t("land_plan_smm_name", "SMM"),
      price: "$25",
      period: t("land_plan_smm_period", "/month"),
      description: t("land_plan_smm_desc", "For social media managers"),
      features: [
        t("land_plan_smm_f1", "10 social accounts"),
        t("land_plan_smm_f2", "200 scheduled posts/month"),
        t("land_plan_smm_f3", "10 link-in-bio pages"),
        t("land_plan_smm_f4", "200 short links"),
        t("land_plan_smm_f5", "500 AI generations"),
        t("land_plan_smm_f6", "5 GB storage"),
        t("land_plan_smm_f7", "3 team members"),
        t("land_plan_smm_f8", "Approval workflows"),
      ],
      cta: t("land_plan_smm_cta", "Start Free Trial"),
      highlighted: true,
    },
    {
      name: t("land_plan_agency_name", "Agency"),
      price: "$65",
      period: t("land_plan_agency_period", "/month"),
      description: t("land_plan_agency_desc", "For agencies managing clients"),
      features: [
        t("land_plan_agency_f1", "25 social accounts"),
        t("land_plan_agency_f2", "1,000 scheduled posts/month"),
        t("land_plan_agency_f3", "50 link-in-bio pages"),
        t("land_plan_agency_f4", "1,000 short links"),
        t("land_plan_agency_f5", "2,000 AI generations"),
        t("land_plan_agency_f6", "20 GB storage"),
        t("land_plan_agency_f7", "10 team members"),
        t("land_plan_agency_f8", "Unified inbox"),
      ],
      cta: t("land_plan_agency_cta", "Start Free Trial"),
      highlighted: false,
    },
    {
      name: t("land_plan_pro_name", "Pro"),
      price: "$120",
      period: t("land_plan_pro_period", "/month"),
      description: t("land_plan_pro_desc", "For power users & enterprises"),
      features: [
        t("land_plan_pro_f1", "50 social accounts"),
        t("land_plan_pro_f2", "Unlimited scheduled posts"),
        t("land_plan_pro_f3", "100 link-in-bio pages"),
        t("land_plan_pro_f4", "Unlimited short links"),
        t("land_plan_pro_f5", "Unlimited AI generations"),
        t("land_plan_pro_f6", "100 GB storage"),
        t("land_plan_pro_f7", "20 team members"),
        t("land_plan_pro_f8", "Priority support"),
      ],
      cta: t("land_plan_pro_cta", "Start Free Trial"),
      highlighted: false,
    },
  ];

  const FEATURES = [
    {
      icon: Send,
      title: t("land_feat_pub_title", "Multi-Platform Publishing"),
      description: t("land_feat_pub_desc", "Publish to YouTube, Instagram, TikTok, X, LinkedIn, Facebook, Pinterest, and more from one dashboard."),
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: Calendar,
      title: t("land_feat_sched_title", "Smart Scheduling"),
      description: t("land_feat_sched_desc", "Schedule posts for optimal times with our AI-powered scheduling engine. Drag and drop on the visual calendar."),
      color: "bg-emerald-50 text-emerald-600",
    },
    {
      icon: BarChart3,
      title: t("land_feat_analytics_title", "Unified Analytics"),
      description: t("land_feat_analytics_desc", "Track performance across all platforms in one place. Engagement, reach, growth, and custom reports."),
      color: "bg-purple-50 text-purple-600",
    },
    {
      icon: Sparkles,
      title: t("land_feat_ai_title", "AI Content Assistant"),
      description: t("land_feat_ai_desc", "Generate captions, hashtags, and content ideas. Our AI learns your brand voice and writes in your style."),
      color: "bg-amber-50 text-amber-600",
    },
    {
      icon: Layers,
      title: t("land_feat_link_title", "Link-in-Bio Pages"),
      description: t("land_feat_link_desc", "Build beautiful, customizable landing pages for your social profiles. No coding required."),
      color: "bg-pink-50 text-pink-600",
    },
    {
      icon: Link2,
      title: t("land_feat_short_title", "Smart Short Links"),
      description: t("land_feat_short_desc", "Shorten, brand, and track every link. See clicks, locations, and referrers in real-time."),
      color: "bg-indigo-50 text-indigo-600",
    },
    {
      icon: Users,
      title: t("land_feat_collab_title", "Team Collaboration"),
      description: t("land_feat_collab_desc", "Invite team members, assign roles, set up approval workflows. Perfect for agencies and teams."),
      color: "bg-teal-50 text-teal-600",
    },
    {
      icon: Globe,
      title: t("land_feat_inbox_title", "Unified Inbox"),
      description: t("land_feat_inbox_desc", "Manage all your DMs, comments, and mentions from every platform in one unified inbox."),
      color: "bg-rose-50 text-rose-600",
    },
  ];

  const TESTIMONIALS = [
    {
      name: t("land_test_1_name", "Sarah Chen"),
      role: t("land_test_1_role", "Social Media Manager"),
      company: t("land_test_1_company", "Bloom Agency"),
      quote: t("land_test_1_quote", "PlugD cut our posting time by 70%. The multi-platform scheduling and approval workflows are game-changers for our team."),
      avatar: "SC",
      rating: 5,
    },
    {
      name: t("land_test_2_name", "Marcus Johnson"),
      role: t("land_test_2_role", "Content Creator"),
      company: t("land_test_2_company", "250K+ followers"),
      quote: t("land_test_2_quote", "The AI caption writer and best-time suggestions have genuinely increased my engagement. It's like having a personal social media strategist."),
      avatar: "MJ",
      rating: 5,
    },
    {
      name: t("land_test_3_name", "Priya Patel"),
      role: t("land_test_3_role", "Marketing Director"),
      company: t("land_test_3_company", "TechVista"),
      quote: t("land_test_3_quote", "We manage 40+ client accounts through PlugD. The workspace feature and analytics reporting save us hours every week."),
      avatar: "PP",
      rating: 5,
    },
  ];

  const FAQS = [
    {
      q: t("land_faq_1_q", "How many social accounts can I connect?"),
      a: t("land_faq_1_a", "It depends on your plan. Free allows 3 accounts, Start gives you 10, SMM gives 25, and Agency supports up to 75 social accounts across all platforms."),
    },
    {
      q: t("land_faq_2_q", "Which social media platforms do you support?"),
      a: t("land_faq_2_a", "We support YouTube, Instagram, Facebook, X (Twitter), TikTok, LinkedIn, Pinterest, Threads, Bluesky, Telegram, Tumblr, WordPress, and Google My Business."),
    },
    {
      q: t("land_faq_3_q", "Can I try PlugD before committing?"),
      a: t("land_faq_3_a", "Absolutely! Our Free plan lets you use core features forever. Paid plans come with a 14-day free trial with no credit card required."),
    },
    {
      q: t("land_faq_4_q", "Does PlugD support team collaboration?"),
      a: t("land_faq_4_a", "Yes! SMM and Agency plans include team features with role-based permissions, approval workflows, and shared content calendars."),
    },
    {
      q: t("land_faq_5_q", "Is my data secure?"),
      a: t("land_faq_5_a", "We use industry-standard encryption, OAuth 2.0 for all platform connections, and never store your social media passwords. Your data is yours."),
    },
  ];

  const PLATFORM_LOGOS = [
    t("land_platform_youtube", "YouTube"),
    t("land_platform_instagram", "Instagram"),
    t("land_platform_facebook", "Facebook"),
    t("land_platform_x", "X"),
    t("land_platform_tiktok", "TikTok"),
    t("land_platform_linkedin", "LinkedIn"),
    t("land_platform_pinterest", "Pinterest"),
    t("land_platform_threads", "Threads"),
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Shared marketing header — Products / AI / Solutions / Resources / Pricing */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 text-emerald-700 rounded-full text-sm font-semibold mb-8 shadow-sm">
              <Sparkles size={15} className="text-emerald-500" />
              <span>{t("land_hero_badge", "AI-Powered Social Media Management")}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight tracking-tight">
              {t("land_hero_title_part1", "Manage all your")}
              <br />
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                {t("land_hero_title_part2", "social media")}
              </span>
              <br />
              {t("land_hero_title_part3", "in one place")}
            </h1>

            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t("land_hero_subtitle", "Schedule posts, track analytics, collaborate with your team, and grow your audience across every platform. All from a single, beautiful dashboard.")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="group flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white text-lg font-semibold rounded-xl hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30"
              >
                {t("land_hero_cta_start", "Start Free — No Card Required")}
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <a
                href="#features"
                className="flex items-center gap-2 px-8 py-4 text-gray-700 text-lg font-medium hover:text-gray-900 transition-colors"
              >
                {t("land_hero_link_see", "See how it works")}
              </a>
            </div>

            {/* Platform bar */}
            <div className="mt-16">
              <p className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-6">
                {t("land_hero_platforms", "Publish to all major platforms")}
              </p>
              <div className="flex items-center justify-center gap-6 sm:gap-10 flex-wrap">
                {PLATFORM_LOGOS.map((platform) => (
                  <div
                    key={platform}
                    className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="text-sm font-medium">{platform}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-20 relative">
            <div className="bg-gradient-to-b from-emerald-50 to-transparent rounded-3xl p-2 sm:p-4">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-100 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-white rounded-md px-4 py-1 text-xs text-gray-400 border border-gray-200">
                      social.plugd.online
                    </div>
                  </div>
                </div>
                <div className="aspect-[16/9] bg-gradient-to-br from-gray-50 via-white to-emerald-50 flex items-center justify-center">
                  <div className="text-center px-8">
                    <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
                      {[
                        { label: "Published", value: "1,247", color: "bg-emerald-50 text-emerald-700" },
                        { label: "Scheduled", value: "38", color: "bg-blue-50 text-blue-700" },
                        { label: "Engagement", value: "89.4K", color: "bg-pink-50 text-pink-700" },
                        { label: "Link Clicks", value: "12.8K", color: "bg-purple-50 text-purple-700" },
                      ].map((stat) => (
                        <div key={stat.label} className={`${stat.color} rounded-xl p-4`}>
                          <p className="text-2xl font-bold">{stat.value}</p>
                          <p className="text-xs font-medium mt-1 opacity-70">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-400 text-sm">Your PlugD Dashboard</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900">
              {t("land_features_heading", "Everything you need to dominate social media")}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {t("land_features_subheading", "From publishing to analytics, PlugD has every tool you need to manage and grow your social media presence.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:border-gray-200 transition-all group"
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feature.color} group-hover:scale-110 transition-transform`}
                  >
                    <Icon size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-emerald-500">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "50K+", label: t("land_stat_users", "Active Users") },
              { value: "5M+", label: t("land_stat_posts", "Posts Published") },
              { value: "13", label: t("land_stat_platforms", "Platforms Supported") },
              { value: "99.9%", label: t("land_stat_uptime", "Uptime") },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl sm:text-4xl font-extrabold text-white">
                  {stat.value}
                </p>
                <p className="text-sm text-emerald-100 mt-1 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900">
              {t("land_pricing_heading", "Simple, transparent pricing")}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {t("land_pricing_subheading", "Start free. Upgrade when you&apos;re ready. No hidden fees, no surprises.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-6 border-2 transition-all ${
                  plan.highlighted
                    ? "border-emerald-500 shadow-xl shadow-emerald-500/10 scale-105"
                    : "border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                    {t("land_pricing_popular", "Most Popular")}
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-sm text-gray-500">{plan.period}</span>
                  </div>
                </div>
                <Link
                  to="/register"
                  className={`block text-center w-full py-3 rounded-lg text-sm font-semibold transition-all ${
                    plan.highlighted
                      ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                >
                  {plan.cta}
                </Link>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <Check
                        size={16}
                        className={`flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? "text-emerald-500" : "text-gray-400"
                        }`}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900">
              {t("land_testimonials_heading", "Loved by creators and teams")}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {t("land_testimonials_subheading", "See why thousands of social media professionals choose PlugD.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="text-amber-400 fill-amber-400"
                    />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">&quot;{t.quote}&quot;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-sm">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">
                      {t.role} at {t.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900">
              {t("land_faq_heading", "Frequently asked questions")}
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-900 pr-4">
                    {faq.q}
                  </span>
                  {openFaq === i ? (
                    <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white">
            {t("land_cta_title", "Ready to level up your social media?")}
          </h2>
          <p className="mt-4 text-lg text-emerald-100 max-w-2xl mx-auto">
            {t("land_cta_subtitle", "Join 50,000+ creators and teams who use PlugD to save time, grow faster, and publish smarter.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="group flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 text-lg font-bold rounded-xl hover:bg-emerald-50 transition-all shadow-lg"
            >
              {t("land_cta_button", "Get Started Free")}
              <ArrowRight
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
          <p className="mt-4 text-sm text-emerald-200">
            {t("land_cta_footer", "No credit card required. Free plan available forever.")}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <PlugDLogo size={32} className="rounded-lg" />
                <span className="font-bold text-lg text-white">PlugD</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                AI-powered social media management platform for creators, teams, and
                agencies.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{t("land_footer_product", "Product")}</h4>
              <ul className="space-y-2.5">
                {[
                  t("land_footer_product_features", "Features"),
                  t("land_footer_product_pricing", "Pricing"),
                  t("land_footer_product_integrations", "Integrations"),
                  t("land_footer_product_changelog", "Changelog"),
                ].map((item) => (
                  <li key={item}>
                    <button type="button" className="text-sm text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0">
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{t("land_footer_company", "Company")}</h4>
              <ul className="space-y-2.5">
                {[
                  t("land_footer_company_about", "About"),
                  t("land_footer_company_blog", "Blog"),
                  t("land_footer_company_careers", "Careers"),
                  t("land_footer_company_contact", "Contact"),
                ].map((item) => (
                  <li key={item}>
                    <button type="button" className="text-sm text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0">
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">{t("land_footer_legal", "Legal")}</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">
                    {t("land_footer_legal_privacy", "Privacy Policy")}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">
                    {t("land_footer_legal_tos", "Terms of Service")}
                  </Link>
                </li>
                <li>
                  <button type="button" className="text-sm text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0">
                    {t("land_footer_legal_cookies", "Cookie Policy")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} {t("land_footer_copyright", "PlugD. All rights reserved.")}
            </p>
            <div className="flex items-center gap-4">
              {[
                t("land_footer_social_twitter", "Twitter"),
                t("land_footer_social_linkedin", "LinkedIn"),
                t("land_footer_social_github", "GitHub"),
              ].map((social) => (
                <button
                  type="button"
                  key={social}
                  className="text-sm text-gray-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0"
                >
                  {social}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
